import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import TranslationService from "../../Services/Translation/TranslationService";
/** Styles Import */
import "./Style.css";
/** Components Import */
import { Form, Image, Alert, Button } from "react-bootstrap";
import { QRCodeCanvas } from "qrcode.react";
import AutoTranslate from "../../Components/AutoTranslate";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMobileAlt,
  faQrcode,
  faRotateRight,
  faExclamationTriangle,
  faCheckCircle,
} from "@fortawesome/free-solid-svg-icons";

const Worker = ({
  qrCode = "",
  onRefresh,
  timer = 0,
  startTimer = false,
  onSwitchToBusiness,
}) => {
  const [showInstructions, setShowInstructions] = useState(true);
  const language = useSelector((state) => state.localStorage.language);

  useEffect(() => {
    const handleResize = () => {
      // 화면 줄였을 때는 가능하게
      if (window.innerWidth > 1020) {
        document.body.style.overflow = "hidden";
      } else {
        document.body.style.overflow = "auto";
      }
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      document.body.style.overflow = "auto";
    };
  }, []);

  return (
    <div className="qr-login-container">
      {/* 헤더 섹션 */}
      <div className="qr-login-header">
        <div className="qr-login-icon">
          <FontAwesomeIcon icon={faQrcode} size="2x" />
        </div>
        <h2 className="qr-login-title">
          <AutoTranslate targetLanguage={language.lang}>
            QR 코드로 로그인
          </AutoTranslate>
        </h2>
        <p className="qr-login-subtitle">
          <AutoTranslate targetLanguage={language.lang}>
            SafeWay 모바일 앱에서 QR 코드를 스캔하여 로그인하세요
          </AutoTranslate>
        </p>
      </div>

      {/* 메인 콘텐츠 - 모바일에서는 세로, 데스크톱에서는 가로 */}
      <div className="qr-main-content">
        {/* QR 코드 스캔 가이드 (왼쪽) */}
        <div className="qr-image-guide">
          <h4 className="guide-title">
            <AutoTranslate targetLanguage={language.lang}>
              QR 코드 스캔 가이드
            </AutoTranslate>
          </h4>
          <div className="guide-images-container">
            <div className="guide-image-item">
              <div className="guide-image-wrapper">
                <img
                  src="/login/main1.jpg"
                  alt="QR 스캔 버튼 위치"
                  className="guide-image"
                  onError={(e) => {
                    e.target.style.display = "none";
                    e.target.nextElementSibling.style.display = "flex";
                  }}
                />
                <div className="guide-image-placeholder">
                  <FontAwesomeIcon icon={faQrcode} size="2x" />
                  <span>
                    <AutoTranslate targetLanguage={language.lang}>
                      QR 스캔 버튼
                    </AutoTranslate>
                  </span>
                </div>
              </div>
              <p className="guide-image-caption">
                <AutoTranslate targetLanguage={language.lang}>
                  1. 앱 상단에 버튼을 클릭
                </AutoTranslate>
              </p>
            </div>

            <div className="guide-image-item">
              <div className="guide-image-wrapper">
                <img
                  src="/login/main2.jpg"
                  alt="QR 코드 스캔"
                  className="guide-image"
                  onError={(e) => {
                    e.target.style.display = "none";
                    e.target.nextElementSibling.style.display = "flex";
                  }}
                />
                <div className="guide-image-placeholder">
                  <FontAwesomeIcon icon={faMobileAlt} size="2x" />
                  <span>
                    <AutoTranslate targetLanguage={language.lang}>
                      QR 코드 스캔
                    </AutoTranslate>
                  </span>
                </div>
              </div>
              <p className="guide-image-caption">
                <AutoTranslate targetLanguage={language.lang}>
                  2. 세이프웨이 로그인 클릭
                </AutoTranslate>
              </p>
            </div>

            <div className="guide-image-item">
              <div className="guide-image-wrapper">
                <img
                  src="/login/main3.jpg"
                  alt="로그인 성공"
                  className="guide-image"
                  onError={(e) => {
                    e.target.style.display = "none";
                    e.target.nextElementSibling.style.display = "flex";
                  }}
                />
                <div className="guide-image-placeholder">
                  <FontAwesomeIcon icon={faCheckCircle} size="2x" />
                  <span>
                    <AutoTranslate targetLanguage={language.lang}>
                      로그인 성공
                    </AutoTranslate>
                  </span>
                </div>
              </div>
              <p className="guide-image-caption">
                <AutoTranslate targetLanguage={language.lang}>
                  3. 버튼을 클릭하여 스캔 시작
                </AutoTranslate>
              </p>
            </div>
          </div>
        </div>

        {/* QR 코드 섹션 (오른쪽) */}
        <div className="qr-code-section">
          {qrCode ? (
            <div className="qr-code-container">
              <div className="qr-code-wrapper">
                <QRCodeCanvas
                  value={qrCode}
                  size={220}
                  level="M"
                  includeMargin={true}
                />
              </div>

              {/* 타이머와 새로고침 버튼을 그룹화 */}
              <div className="qr-code-actions">
                {/* 타이머 표시 */}
                {startTimer && (
                  <div className="qr-timer">
                    <div className="timer-circle">
                      <span className="timer-text">
                        {Math.floor(timer / 60)}:
                        {(timer % 60).toString().padStart(2, "0")}
                      </span>
                    </div>
                    <p className="timer-label">
                      <AutoTranslate targetLanguage={language.lang}>
                        QR 코드 유효 시간
                      </AutoTranslate>
                    </p>
                  </div>
                )}

                {/* 새로고침 버튼 */}
                {startTimer && timer > 0 ? null : (
                  <Button
                    variant="outline-primary"
                    className="refresh-qr-btn"
                    onClick={onRefresh}
                  >
                    <FontAwesomeIcon icon={faRotateRight} className="me-2" />
                    <AutoTranslate targetLanguage={language.lang}>
                      새 QR 코드 생성
                    </AutoTranslate>
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <div className="qr-loading">
              <div className="loading-spinner"></div>
              <p>
                <AutoTranslate targetLanguage={language.lang}>
                  QR 코드를 생성하고 있습니다...
                </AutoTranslate>
              </p>
            </div>
          )}
        </div>
      </div>

      {/* 하단 섹션 - 데스크톱에서는 가로 배치 */}
      <div className="qr-bottom-section">
        {/* 대체 로그인 옵션 */}
        <div className="qr-alternative">
          <p className="alternative-text">
            <AutoTranslate targetLanguage={language.lang}>
              관리자 로그인이신가요?
            </AutoTranslate>
            <Button
              variant="link"
              className="alternative-link p-0 ms-1"
              onClick={onSwitchToBusiness}
            >
              <AutoTranslate targetLanguage={language.lang}>
                관리자 로그인으로 전환
              </AutoTranslate>
            </Button>
          </p>
        </div>

        {/* 주의사항 */}
        <div className="qr-notice">
          <Alert variant="info" className="notice-alert">
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
            <strong>
              <AutoTranslate targetLanguage={language.lang}>
                주의사항:
              </AutoTranslate>
            </strong>
            <AutoTranslate targetLanguage={language.lang}>
              QR 코드는 3분간만 유효하며, 개인정보 보호를 위해 다른 사람과
              공유하지 마세요.
            </AutoTranslate>
          </Alert>
        </div>
      </div>
    </div>
  );
};

export default Worker;
